import { isRecord } from '@fruitful/fruitils';
import type { DatasetItem, MastraClient } from '@mastra/client-js';

const PAGE_SIZE = 100;
const MAX_PAGES = 50;

export interface DatasetCase {
    id: string;
    caseId: string;
    prompt: string;
    extraTurnCount: number;
    groundTruth: string;
    fixtureId: string;
    group: string;
}

export interface DatasetGroup {
    name: string;
    cases: DatasetCase[];
}

export interface DatasetView {
    id: string;
    datasetName: string;
    version: number;
    groups: DatasetGroup[];
}

const readString = (value: unknown): string | null =>
    typeof value === 'string' && value !== '' ? value : null;

const listAll = async <T>(
    load: (page: number) => Promise<{ rows: T[]; hasMore: boolean }>,
): Promise<T[]> => {
    const rows: T[] = [];
    let page = 0;
    for (;;) {
        if (page >= MAX_PAGES) {
            throw new Error('Dataset list did not terminate');
        }
        const listed = await load(page);
        rows.push(...listed.rows);
        if (!listed.hasMore || listed.rows.length === 0) {
            return rows;
        }
        page += 1;
    }
};

const toCase = (item: DatasetItem): DatasetCase => {
    const metadata = isRecord(item.metadata) ? item.metadata : {};
    const prompts = Array.isArray(item.input)
        ? item.input.filter(
              (prompt): prompt is string =>
                  typeof prompt === 'string' && prompt !== '',
          )
        : [];
    const prompt = prompts[0] ?? '';

    return {
        id: item.id,
        caseId: readString(metadata.caseId) ?? item.externalId ?? item.id,
        prompt,
        extraTurnCount: Math.max(prompts.length - 1, 0),
        groundTruth:
            typeof item.groundTruth === 'string' ? item.groundTruth : '',
        fixtureId: readString(metadata.fixtureId) ?? '',
        group: readString(metadata.group) ?? 'Ungrouped',
    };
};

const groupCases = (
    cases: DatasetCase[],
    orderedNames: string[],
): DatasetGroup[] => {
    const byName = new Map<string, DatasetCase[]>();
    for (const evalCase of cases) {
        const group = byName.get(evalCase.group) ?? [];
        group.push(evalCase);
        byName.set(evalCase.group, group);
    }

    const names = [...orderedNames];
    for (const name of byName.keys()) {
        if (!names.includes(name)) {
            names.push(name);
        }
    }

    return names.flatMap((name) => {
        const group = byName.get(name);
        return group == null || group.length === 0
            ? []
            : [{ name, cases: group }];
    });
};

/**
 * The published Sprout dataset for an agent. Null when this environment has
 * no dataset for that agent.
 */
export const findDataset = async (
    client: MastraClient,
    agentId: string,
): Promise<{
    id: string;
    name: string;
    version: number;
    metadata: Record<string, unknown> | null;
} | null> => {
    const datasets = await listAll(async (page) => {
        const listed = await client.listDatasets({ page, perPage: PAGE_SIZE });
        return {
            rows: listed.datasets,
            hasMore: listed.pagination.hasMore,
        };
    });
    const matches = datasets.filter(
        (dataset) => dataset.metadata?.agentId === agentId,
    );
    if (matches.length === 0) {
        return null;
    }
    if (matches.length > 1) {
        throw new Error(`More than one dataset for ${agentId}`);
    }
    const dataset = matches[0];
    if (dataset == null) {
        return null;
    }

    return {
        id: dataset.id,
        name: dataset.name,
        version: dataset.version,
        metadata: dataset.metadata ?? null,
    };
};

/**
 * The published Sprout dataset for an agent, grouped for display. Null when
 * this environment has no dataset for that agent. An experiment is not required.
 */
export const loadDataset = async (
    client: MastraClient,
    agentId: string,
): Promise<DatasetView | null> => {
    const found = await findDataset(client, agentId);
    if (found == null) {
        return null;
    }
    const dataset = found;

    const items = await listAll(async (page) => {
        const listed = await client.listDatasetItems(dataset.id, {
            page,
            perPage: PAGE_SIZE,
        });
        return {
            rows: listed.items,
            hasMore: listed.pagination.hasMore,
        };
    });
    const rawGroups: unknown = dataset.metadata?.groups;
    const orderedNames = Array.isArray(rawGroups)
        ? rawGroups.filter((name): name is string => readString(name) != null)
        : [];

    return {
        id: dataset.id,
        datasetName: dataset.name,
        version: dataset.version,
        groups: groupCases(items.map(toCase), orderedNames),
    };
};
